import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

// Utils
import cn from '../../../utils/tools/cn';
import useScroll from '../../../utils/hooks/useScroll';

// Icons
import { ReactComponent as ChevronDown } from '../../../assets/icons/chevron_down.svg';

import styles from './DropdownActions.module.scss';

function DropdownActions({
  id, label, options, rightAlign, className,
}) {
  const { lockBodyScroll } = useScroll();

  const optionsRef = useRef();
  const wrapperRef = useRef();
  const dropdownRef = useRef();

  const [isOpened, setIsOpened] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [currentBodyWidth] = useState(window.innerWidth);

  const handleClickOutside = (e) => {
    if (
      optionsRef?.current && !optionsRef.current.contains(e.target)
      && wrapperRef?.current && !wrapperRef.current.contains(e.target)
      && dropdownRef?.current && !dropdownRef.current.contains(e.target)
    ) {
      setIsOpened(false);
    }
  };

  const handleCleanUnused = () => {
    const elements = document.querySelectorAll('.dropdownActions_options');
    if (elements?.length) {
      elements.forEach((element) => {
        const elmId = element.id?.split('_')?.[0];
        if (elmId === id && element.id !== `${id}_${currentBodyWidth}`) element.remove();
      });
    }
  };

  useEffect(() => {
    setTimeout(() => {
      if (optionsRef?.current
        && wrapperRef?.current
        && !document.querySelector(`#${id}_${window.innerWidth}`)
      ) {
        document.querySelector('#dropdownActions_holder').appendChild(optionsRef.current);
        optionsRef.current.id = `${id}_${window.innerWidth}`;

        optionsRef.current.addEventListener('mouseleave', (e) => {
          if (e.relatedTarget !== dropdownRef?.current) setIsOpened(false);
        });

        setInitialized(true);
      }
    }, 400);

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      if (optionsRef?.current) {
        optionsRef.current.removeEventListener('mouseleave', () => setIsOpened(false));
        document.removeEventListener('mousedown', handleClickOutside);
      }

      if (document.querySelector(`#${id}_${currentBodyWidth}`)) {
        document.querySelector(`#${id}_${currentBodyWidth}`).remove();
      }

      lockBodyScroll(false);
    };
  }, []);

  useEffect(() => {
    if (initialized) handleCleanUnused();
  }, [initialized]);

  const getOptionsStyle = () => {
    if (wrapperRef?.current?.getBoundingClientRect() && document?.body) {
      return {
        left: rightAlign ? 'initial' : `${wrapperRef.current.offsetLeft}px`,
        right: rightAlign
          ? `${window.innerWidth - (wrapperRef.current.offsetLeft + wrapperRef.current.offsetWidth)}px`
          : 'initial',
        top: `${wrapperRef.current.getBoundingClientRect().top + wrapperRef.current.offsetHeight}px`,
      };
    }
    return false;
  };

  useEffect(() => {
    lockBodyScroll(isOpened && initialized && getOptionsStyle());
  }, [isOpened]);

  return (
    <>
      <button
        type="button"
        ref={wrapperRef}
        onMouseEnter={() => setIsOpened(true)}
        onMouseLeave={(e) => {
          if (e.relatedTarget !== optionsRef?.current) setIsOpened(false);
        }}
        onFocus={() => setIsOpened(true)}
        onBlur={() => setIsOpened(false)}
        className={cn([styles.wrapper, 'd-inline-flex', className || ''])}
        disabled={!initialized || !getOptionsStyle()}
      >
        <div
          ref={dropdownRef}
          className={cn([styles.dropdown, initialized ? 'c-pointer' : ''])}
        >
          <div className={cn([styles.label, 'd-flex a-center fs-14 c-primary-500 fw-600 ws-nowrap'])}>
            <ChevronDown className="m-r-10" />
            {label}
          </div>
        </div>
      </button>

      <ul
        ref={optionsRef}
        style={getOptionsStyle() || {}}
        className={cn([
          styles.options,
          rightAlign ? styles.right : '',
          getOptionsStyle() && initialized && isOpened ? styles.show : '',
          'dropdownActions_options',
        ])}
      >
        {options?.map((option, index) => (
          <li key={index}>
            <a
              href={option.href}
              className={cn([styles.link, 'c-primary-500 fw-600 fs-16'])}
            >
              {option.label}
            </a>
          </li>
        ))}
      </ul>
    </>
  );
}

DropdownActions.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  rightAlign: PropTypes.bool,
  className: PropTypes.string,
};

DropdownActions.defaultProps = {
  options: [],
  rightAlign: false,
  className: null,
};

export default DropdownActions;
